import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
    AnimatedLoader,
    ErrorAlert,
    HorizontalFlex,
    icons,
    Input,
    PadContainer,
    VerticalFlex,
} from 'smartsuite-ui';
import { v4 as uuidv4 } from 'uuid';
import { updateEnrollmentUri } from '../constants/apiConstants';
import AddressForm from '../components/AddressForm';
import { isEmailValidFormat, isPhoneValid } from '../utils/validationUtil';
import { putDataPromise } from 'brock-fetch-utils/src/putDataPromise';
import { countriesEnglish } from '../constants/countries';
import { formatPhoneNumber } from '../utils/formatters';

function ModifyContactDetailsPage() {
    const { t } = useTranslation();
    const { reservationId, reservationLastName } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const enrollmentWithUpdatedPassengers = queryClient.getQueryData([
        'enrollment',
        reservationId,
        reservationLastName,
        'stateData',
    ]);

    // Send back to the modify flight details page if passengers missing
    useEffect(() => {
        if (!enrollmentWithUpdatedPassengers?.passengers) {
            navigate('../modifyFlightDetails');
        }
    }, [navigate, enrollmentWithUpdatedPassengers?.passengers]);

    const navigateToEnrollmentCard = (httpStatus) => {
        navigate('/waltdisneyworld', { state: { errorStatus: httpStatus } });
        return;
    };

    const addressFormData = enrollmentWithUpdatedPassengers?.addresses.map(
        (passenger) => ({
            ...passenger,
            id: uuidv4(),
            addressLine2: passenger?.addressLine2 || '',
            country: countriesEnglish.find(
                (country) => country.valueField === passenger?.country,
            ),
            numberOfTags: String(passenger.numberOfTags),
        }),
    );
    const [email, setEmail] = useState(
        enrollmentWithUpdatedPassengers?.primaryEmail,
    );
    const [phoneNumberField, setPhoneNumberField] = useState(
        formatPhoneNumber(enrollmentWithUpdatedPassengers?.primaryPhone),
    );
    const phoneNumber = phoneNumberField.replace(/[^\d]/g, '');

    const updateEnrollmentMutation = useMutation({
        mutationFn: (payload) => putDataPromise(updateEnrollmentUri(), payload),
        onSuccess: () => {
            navigate('..', { state: { hasModified: true } });
        },
        onError: (error) => {
            // putDataPromise error object doesn't contain same properties as postDataPromise therefore hardcoded to 500
            navigateToEnrollmentCard(500);
        },
    });

    const handleClickBack = () => {
        navigate(`../modifyFlightDetails`);
    };

    const handleSubmit = () => {
        queryClient.invalidateQueries([
            'enrollment',
            reservationId,
            reservationLastName,
        ]);

        updateEnrollmentMutation.mutate({
            reservationId: reservationId,
            primaryEmail: email,
            primaryPhone: phoneNumber,
            passengers: enrollmentWithUpdatedPassengers.passengers,
        });
    };

    if (!reservationId) {
        return (
            <ErrorAlert type="error">
                <strong className="error-title">
                    {t('reservationNumberInvalidHeading')}
                </strong>
            </ErrorAlert>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="modify-contact-details">
                <header className="modify-contact-details__header">
                    <div className="modify-contact-details__title">
                        {t('modifyContactDetailsTitle')}
                    </div>
                </header>
                <div>
                    <p>
                        <Trans
                            i18nKey={'modifyContactDetailsDescription'}
                            components={[
                                // eslint-disable-next-line jsx-a11y/anchor-has-content
                                <a href="mailto:bcsupport@bagcheck.com" />,
                            ]}
                        />
                    </p>
                </div>
                <div className="contact-information">
                    <div className="contact-information__title">
                        {t('contactInformationTitle')}
                    </div>
                    <p>{t('contactInformationDescription')}</p>
                    <Input
                        label={`${t('emailAddress')} *`}
                        errorMessage={`${t('emailAddress')} ${t(
                            'inputInvalid',
                        )}`}
                        id="email"
                        isValid={!email || isEmailValidFormat(email)}
                        onChange={(event) => setEmail(event.target.value)}
                        value={email}
                        required
                    />
                    <Input
                        label={`${t('phoneNumber')} *`}
                        id="phoneNumber"
                        isValid={!phoneNumber || isPhoneValid(phoneNumber)}
                        value={phoneNumberField}
                        onChange={(event) => {
                            setPhoneNumberField(
                                formatPhoneNumber(event.target.value),
                            );
                        }}
                        errorMessage={`${t('phoneNumber')} ${t(
                            'inputInvalid',
                        )}`}
                        type="tel"
                        placeholder="(###) ###-####"
                        maxLength={14}
                        required
                    />
                </div>
                <PadContainer cssClassModifiers={['pad-small']} />
                <VerticalFlex>
                    {addressFormData?.map((data, index) => (
                        <div key={data.id}>
                            {index > 0 && (
                                <PadContainer
                                    cssClassModifiers={['pad-small']}
                                />
                            )}
                            <AddressForm data={data} index={index} disabled />
                        </div>
                    ))}
                    <PadContainer cssClassModifiers={['pad-small']} />

                    <p>{t('phoneNumberDisclaimer')}</p>
                    <PadContainer cssClassModifiers={['pad-small']} />
                    <div className="modify-contact-details__navigate-btns-container">
                        <button
                            type="button"
                            className="btn btn--navigation"
                            onClick={handleClickBack}
                        >
                            <span className={icons.arrowLeft} />
                            <span>Back</span>
                        </button>
                        <button
                            type="button"
                            className="btn btn--primary"
                            onClick={handleSubmit}
                            disabled={
                                !isEmailValidFormat(email) ||
                                !isPhoneValid(phoneNumber) ||
                                updateEnrollmentMutation.isLoading
                            }
                        >
                            {updateEnrollmentMutation.isLoading ? (
                                <HorizontalFlex
                                    cssClassModifiers={['justify-center']}
                                >
                                    <AnimatedLoader />
                                </HorizontalFlex>
                            ) : (
                                <>
                                    <span>{t('submitChanges')}</span>
                                    <span className={icons.arrowRight} />
                                </>
                            )}
                        </button>
                    </div>
                </VerticalFlex>
            </div>
        </div>
    );
}

export default ModifyContactDetailsPage;
